/*
  仕上がりの3Dプレビュー。**ブラウザだけで完結する。**

  国土地理院の標高タイルをその場で読み、標高からメッシュを組んで WebGL で描く。
  サーバは持たない。地理院タイルは Access-Control-Allow-Origin: * を返すので、
  canvas に描いて画素を読み出せる（実測で確認済み）。

  **これはプレビューであって製品ではない。**
    - 標高は 10mメッシュ（dem_png・全国を覆う）を z14 で読む。製品は5mメッシュを使う
    - 格子は製品（0.3mm）よりはるかに粗い
    - 等高線と背面の刻印は描かない（この粗さでは出ない）
  画面にもその旨を出すこと。混同されると「届いた物が違う」になる。

  Three.js は **assets/ から**動的 import する（2026-08-22に unpkg から移した。
  外部が落ちるとステップ3が動かず、**唯一の確認機会が失われる**ため）。
  ステップ3を開いたときに初めて読む（1.27MB あるので、開かない人には読ませない）。
*/
(function () {
  'use strict';

  /* **URLは Liquid から渡る。** assets/ のファイルは Shopify のCDN上で版ごとに
     URLが変わるので、ここに直書きできない。デザイナーの root の data 属性から取る
     （`data-mountains-url` と同じ手）。**読むのは `loadThree()` の中**で、
     スクリプトの読み込み順に左右されないようにしてある。 */
  function threeUrl() {
    const el = document.querySelector('[data-r3d-designer]');
    return (el && el.dataset.threeUrl) || '';
  }
  const DEM_TILE = 'https://cyberjapandata.gsi.go.jp/xyz/dem_png/{z}/{x}/{y}.png';
  const DEM_Z = 14;
  const TILE = 256;

  /* 模型の基部の厚み[mm]。製造側 ProductSpec.base_mm と同じ値。 */
  const BASE_MM = 11.0;
  /* ルートの浮き彫り。製造側 route_w_mm / route_h_mm と同じ。
     ⚠️ 3.0 / 1.8 から 2.4 / 0.84 へ（2026-09-09。実物2枚を比べて決定）。 */
  const ROUTE_W_MM = 2.4;
  const ROUTE_H_MM = 0.84;
  /*
    ⚠️⚠️⚠️ **描く帯の幅は `ROUTE_W_MM` ではない。**
    **隆起は smoothstep の山で、色が付くのは「頂の0.35倍より高いセル」だけ。**
    **裾は隆起としては在るが、色は付かない。**
    ⚠️ **式では出せない。** **`0.6012 × route_w` を 0.2mm 格子（`px_mm`）に
    丸めた値**で、丸めの結果 **2.40〜2.64mm はどれも同じ 1.82mm** になる
    （2.00 は 1.61mm、3.00 は 2.19mm）。
    ⭐ **製造側の実コード（`core/product.py` の `route_cells`）で実測した値。**
    ⚠️ **`ROUTE_W_MM` を変えたら測り直すこと。段が変わる境目は 2.40 と 2.68。**
  */
  const ROUTE_BAND_MM = 1.82;
  /* 格子の分割数（長辺方向）。製品ではなくプレビューの粗さ。 */
  const GRID = 200;

  let THREE = null;
  const tileCache = new Map();

  function loadThree() {
    if (THREE) return Promise.resolve(THREE);
    const url = threeUrl();
    if (!url) return Promise.reject(new Error('three-url が渡っていない'));
    return import(url).then(function (m) {
      THREE = m;
      return m;
    });
  }

  /* --- 標高タイル ---------------------------------------------------- */

  function lonToTileX(lon, z) {
    return ((lon + 180) / 360) * Math.pow(2, z);
  }

  function latToTileY(lat, z) {
    const la = (lat * Math.PI) / 180;
    return (
      ((1 - Math.log(Math.tan(la) + 1 / Math.cos(la)) / Math.PI) / 2) *
      Math.pow(2, z)
    );
  }

  /*
    標高タイル1枚を Float32Array(256*256) にして返す。
    地理院の仕様: x = R*65536 + G*256 + B。x < 2^23 なら h = x*0.01、
    x == 2^23 は無効値、それ以上は (x - 2^24)*0.01（海面下）。
  */
  function fetchTile(z, x, y) {
    const key = z + '/' + x + '/' + y;
    if (tileCache.has(key)) return tileCache.get(key);

    const p = new Promise(function (resolve) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        const c = document.createElement('canvas');
        c.width = TILE;
        c.height = TILE;
        const ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        const d = ctx.getImageData(0, 0, TILE, TILE).data;
        const out = new Float32Array(TILE * TILE);
        for (let i = 0, j = 0; i < out.length; i++, j += 4) {
          const v = d[j] * 65536 + d[j + 1] * 256 + d[j + 2];
          out[i] = v === 8388608 ? NaN : v < 8388608 ? v * 0.01 : (v - 16777216) * 0.01;
        }
        resolve(out);
      };
      // 未整備・通信断はまとめて「標高なし」にする。呼び手が穴埋めする
      img.onerror = function () {
        resolve(null);
      };
      img.src = DEM_TILE.replace('{z}', z).replace('{x}', x).replace('{y}', y);
    });

    tileCache.set(key, p);
    return p;
  }

  /* 範囲を覆うタイルをまとめて取り、緯度経度から標高を引ける関数を返す */
  async function makeSampler(bounds, z) {
    const x0 = Math.floor(lonToTileX(bounds.west, z));
    const x1 = Math.floor(lonToTileX(bounds.east, z));
    const y0 = Math.floor(latToTileY(bounds.north, z));
    const y1 = Math.floor(latToTileY(bounds.south, z));

    const jobs = [];
    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        jobs.push(
          fetchTile(z, tx, ty).then(function (data) {
            return { tx: tx, ty: ty, data: data };
          })
        );
      }
    }
    const tiles = await Promise.all(jobs);
    const map = new Map();
    tiles.forEach(function (t) {
      map.set(t.tx + '/' + t.ty, t.data);
    });

    return function (lat, lon) {
      const fx = lonToTileX(lon, z);
      const fy = latToTileY(lat, z);
      const tx = Math.floor(fx);
      const ty = Math.floor(fy);
      const data = map.get(tx + '/' + ty);
      if (!data) return NaN;
      const px = Math.min(TILE - 1, Math.max(0, Math.floor((fx - tx) * TILE)));
      const py = Math.min(TILE - 1, Math.max(0, Math.floor((fy - ty) * TILE)));
      return data[py * TILE + px];
    };
  }

  /*
    等高線の推奨間隔を出すための地形の統計。**3Dとは別に持つ。**
    3Dの格子（GRID=200）から測ると、広い範囲では1コマが数十mになって斜面がなまり、
    急斜面を緩く見積もる。ここは基線を10m（DEMの刻み）に固定して測る。

    返すのは斜度のヒストグラム。**割合そのものを返さないのは、しきい値が
    模型の縮尺（＝選んだサイズ）で変わるから。** サイズを変えるたびに
    測り直さずに済むよう、分布のまま渡して呼び手が切る。
  */
  const STAT_MAX = 320;        /* 統計に使う格子の最大辺。320×320で約10万点 */
  const SLOPE_BASE_M = 10;     /* 斜度を測る基線[m]。DEM（10mメッシュ）の刻みに合わせる */
  const SLOPE_BINS = 400;      /* 0〜4.0 m/m を0.01刻み。4.0超（76°以上）は最終ビンに入れる */
  const SLOPE_STEP = 0.01;

  async function terrainStats(crop) {
    const sample = await makeSampler(crop, DEM_Z);
    const spanLonM = Math.max(crop.widthM, 1);
    const spanLatM = Math.max(crop.heightM, 1);

    const nx = Math.min(STAT_MAX, Math.max(16, Math.round(spanLonM / SLOPE_BASE_M)));
    const ny = Math.min(STAT_MAX, Math.max(16, Math.round(spanLatM / SLOPE_BASE_M)));

    /* 基線10mを緯度経度の差に直す。範囲は数kmなので線形近似で足りる */
    const dLat = (SLOPE_BASE_M / spanLatM) * (crop.north - crop.south);
    const dLon = (SLOPE_BASE_M / spanLonM) * (crop.east - crop.west);

    const hist = new Float64Array(SLOPE_BINS);
    let n = 0;
    let min = Infinity;
    let max = -Infinity;

    for (let j = 0; j < ny; j++) {
      const lat = crop.north - ((j + 0.5) / ny) * (crop.north - crop.south);
      for (let i = 0; i < nx; i++) {
        const lon = crop.west + ((i + 0.5) / nx) * (crop.east - crop.west);
        const zc = sample(lat, lon);
        if (!isFinite(zc)) continue;
        if (zc < min) min = zc;
        if (zc > max) max = zc;

        const ze = sample(lat, lon + dLon);
        const zw = sample(lat, lon - dLon);
        const zn = sample(lat + dLat, lon);
        const zs = sample(lat - dLat, lon);
        if (!isFinite(ze) || !isFinite(zw) || !isFinite(zn) || !isFinite(zs)) continue;

        const gx = (ze - zw) / (2 * SLOPE_BASE_M);
        const gy = (zn - zs) / (2 * SLOPE_BASE_M);
        const g = Math.hypot(gx, gy);
        const b = Math.min(SLOPE_BINS - 1, Math.floor(g / SLOPE_STEP));
        hist[b] += 1;
        n += 1;
      }
    }

    if (!n || !isFinite(min)) return null;

    return {
      minM: min,
      maxM: max,
      n: n,
      /* 斜度が s [m/m] を超える点の割合。等高線が潰れる面積の見積もりに使う */
      steeperThan: function (s) {
        const from = Math.min(SLOPE_BINS, Math.max(0, Math.ceil(s / SLOPE_STEP)));
        let c = 0;
        for (let b = from; b < SLOPE_BINS; b++) c += hist[b];
        return c / n;
      },
    };
  }

  /* --- 形状 ---------------------------------------------------------- */

  /*
    標高の格子を作る。返す z は「模型上のmm」。
    mm_per_m は長辺を実寸に合わせた縮尺で、製造側 build() と同じ決め方
    （長い方の辺に長辺サイズを当てる）。高さは誇張しない（真スケール）。
  */
  async function buildHeight(crop, dims, onProgress) {
    const spanLatM = crop.heightM;
    const spanLonM = crop.widthM;
    const longMm = Math.max(dims.widthMm, dims.depthMm);
    const mmPerM = longMm / Math.max(spanLonM, spanLatM);

    const landscape = spanLonM >= spanLatM;
    const nx = landscape ? GRID : Math.max(8, Math.round((spanLonM / spanLatM) * GRID));
    const ny = landscape ? Math.max(8, Math.round((spanLatM / spanLonM) * GRID)) : GRID;

    if (onProgress) onProgress('標高データを読み込んでいます…');
    const sample = await makeSampler(crop, DEM_Z);

    if (onProgress) onProgress('地形を組み立てています…');
    const z = new Float32Array(nx * ny);
    let min = Infinity;
    let max = -Infinity;
    for (let j = 0; j < ny; j++) {
      const lat = crop.north - ((j + 0.5) / ny) * (crop.north - crop.south);
      for (let i = 0; i < nx; i++) {
        const lon = crop.west + ((i + 0.5) / nx) * (crop.east - crop.west);
        const v = sample(lat, lon);
        z[j * nx + i] = v;
        if (isFinite(v)) {
          if (v < min) min = v;
          if (v > max) max = v;
        }
      }
    }
    if (!isFinite(min)) return null;      // 1点も取れなければあきらめる

    // 穴（無効値）は最低標高で埋める。プレビューなので厳密でなくてよい
    for (let i = 0; i < z.length; i++) if (!isFinite(z[i])) z[i] = min;

    return {
      nx: nx,
      ny: ny,
      z: z,
      minM: min,
      maxM: max,
      mmPerM: mmPerM,
      widthMm: nx > 0 ? Math.max(spanLonM, 0) * mmPerM : 0,
      depthMm: Math.max(spanLatM, 0) * mmPerM,
    };
  }

  /*
    地形の板と、そこから真下へ落とす側面・底面をまとめて1つのジオメトリにする。
    側面を付けないと、斜めから見たときに紙のように見えて「模型」に見えない。
  */
  function terrainGeometry(h) {
    const nx = h.nx;
    const ny = h.ny;
    const w = h.widthMm;
    const d = h.depthMm;
    const top = new Float32Array(nx * ny);
    for (let i = 0; i < top.length; i++) {
      top[i] = (h.z[i] - h.minM) * h.mmPerM + BASE_MM;
    }

    const pos = [];
    const idx = [];
    const at = function (i, j) {
      return [(i / (nx - 1) - 0.5) * w, top[j * nx + i], (j / (ny - 1) - 0.5) * d];
    };

    // 上面
    for (let j = 0; j < ny; j++) {
      for (let i = 0; i < nx; i++) {
        const p = at(i, j);
        pos.push(p[0], p[1], p[2]);
      }
    }
    for (let j = 0; j < ny - 1; j++) {
      for (let i = 0; i < nx - 1; i++) {
        const a = j * nx + i;
        const b = a + 1;
        const c = a + nx;
        const e = c + 1;
        idx.push(a, c, b, b, c, e);
      }
    }

    // 側面（4辺）と底面。上面の頂点を再利用せず、法線が混ざらないよう別に積む
    const base = pos.length / 3;
    const skirt = function (pts) {
      const s = pos.length / 3;
      pts.forEach(function (p) {
        pos.push(p[0], p[1], p[2]);
        pos.push(p[0], 0, p[2]);
      });
      for (let k = 0; k < pts.length - 1; k++) {
        const a = s + k * 2;
        idx.push(a, a + 1, a + 2, a + 2, a + 1, a + 3);
      }
    };
    const north = [];
    const south = [];
    for (let i = 0; i < nx; i++) {
      north.push(at(i, 0));
      south.push(at(i, ny - 1));
    }
    const west = [];
    const east = [];
    for (let j = 0; j < ny; j++) {
      west.push(at(0, j));
      east.push(at(nx - 1, j));
    }
    skirt(north.slice().reverse());
    skirt(south);
    skirt(west);
    skirt(east.slice().reverse());

    // 底面
    const b0 = pos.length / 3;
    pos.push(-w / 2, 0, -d / 2, w / 2, 0, -d / 2, w / 2, 0, d / 2, -w / 2, 0, d / 2);
    idx.push(b0, b0 + 1, b0 + 2, b0, b0 + 2, b0 + 3);

    void base;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setIndex(idx);
    g.computeVertexNormals();
    return g;
  }

  /* ルートを地形の上に載せる帯。トラックごとに分けて色を当てられるようにする

     **重なった区間は、先のまとまり（最初に歩いた日）を上に出す**（2026-08-29）。
     帯はどれも「地形高さ + BASE_MM + ROUTE_H_MM」の同じ高さに置いていたので、
     ⚠️ **同じ道を2日歩くと帯が完全に同一平面になり、z-fighting でどちらが出るかが
     GPU任せだった。** 見え方が不定なうえ、製造側の規則とも一致していなかった。

     まとまりの番号ぶんだけ Y を下げれば、番号の小さい方が必ず上に出る。
     ⭐ **製造側（`manufacture/core/product.py` の owner）と同じ「先勝ち」。**
     顧客への説明は「複数の日が同じ道を通った区間は、最初に歩いた日の色で
     お作りします」（FAQに載せてある）。
     ⚠️ **段差は表示のためだけのもので、造形には出ない。** 製造側は別々に
     ハイトフィールドを組むので、ここでYを触っても3MFは変わらない。 */
  const TRACK_LIFT_MM = 0.004;   /* 隆起0.84mmに対して0.5%。目には見えない */

  function routeGeometries(h, crop, tracks) {
    const w = h.widthMm;
    const d = h.depthMm;
    /* ⚠️ **`ROUTE_W_MM` ではなく帯の幅を使う**（2026-09-09に直した）。
       **以前は隆起の裾まで色を塗っていて、実物より太い線を見せていた。** */
    const half = ROUTE_BAND_MM / 2;
    const topAt = function (lat, lon, lift) {
      const fi = ((lon - crop.west) / (crop.east - crop.west)) * (h.nx - 1);
      const fj = ((crop.north - lat) / (crop.north - crop.south)) * (h.ny - 1);
      const i = Math.min(h.nx - 1, Math.max(0, Math.round(fi)));
      const j = Math.min(h.ny - 1, Math.max(0, Math.round(fj)));
      return {
        x: (fi / (h.nx - 1) - 0.5) * w,
        z: (fj / (h.ny - 1) - 0.5) * d,
        y: (h.z[j * h.nx + i] - h.minM) * h.mmPerM + BASE_MM + ROUTE_H_MM + lift,
      };
    };

    return tracks.map(function (pts, ti) {
      /* 先のまとまりほど高い。0番が最も高く、以降は少しずつ下がる */
      const lift = (tracks.length - 1 - ti) * TRACK_LIFT_MM;
      const p3 = pts.map(function (p) {
        return topAt(p.lat, p.lon, lift);
      });
      const pos = [];
      const idx = [];
      for (let k = 0; k < p3.length; k++) {
        const a = p3[Math.max(0, k - 1)];
        const b = p3[Math.min(p3.length - 1, k + 1)];
        let dx = b.x - a.x;
        let dz = b.z - a.z;
        const len = Math.hypot(dx, dz) || 1;
        dx /= len;
        dz /= len;
        const c = p3[k];
        pos.push(c.x - dz * half, c.y, c.z + dx * half);
        pos.push(c.x + dz * half, c.y, c.z - dx * half);
      }
      for (let k = 0; k < p3.length - 1; k++) {
        const a = k * 2;
        idx.push(a, a + 2, a + 1, a + 1, a + 2, a + 3);
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      g.setIndex(idx);
      g.computeVertexNormals();
      return g;
    });
  }

  /* --- 表示 ---------------------------------------------------------- */

  function Viewer(host) {
    this.host = host;
    this.scene = null;
    this.meshes = [];
  }

  Viewer.prototype.init = function () {
    if (this.renderer) return;
    const w = this.host.clientWidth || 480;
    const h = this.host.clientHeight || 360;

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    /* **第3引数の false が要る。** true（既定）だと Three が canvas に
       インラインの width/height を書き、それが枠の高さを決めてしまう。
       枠は CSS の aspect-ratio で高さを決める作りなので、インラインが入ると
       「canvasが枠を広げ、広がった枠からcanvasの寸法を測る」で縮まなくなる。 */
    this.renderer.setSize(w, h, false);
    this.host.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(38, w / h, 1, 5000);

    /* マット素材なので、鏡面反射はほぼ切って陰影だけで地形を読ませる。
       光沢を足すと、斜面の向きと無関係なハイライトが出て起伏が読めなくなる
       （素材をマットに決めたのと同じ理由）。 */
    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x444444, 1.5));
    const key = new THREE.DirectionalLight(0xffffff, 2.0);
    key.position.set(-1, 1.6, 0.9);
    this.scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.5);
    fill.position.set(1, 0.6, -0.8);
    this.scene.add(fill);

    this.group = new THREE.Group();
    this.scene.add(this.group);

    this.orbit = { az: -0.6, el: 0.85, dist: 300, target: 0 };
    this.bindControls();

    const self = this;
    this.onResize = function () {
      const ww = self.host.clientWidth || 480;
      const hh = self.host.clientHeight || 360;
      self.renderer.setSize(ww, hh, false);
      self.camera.aspect = ww / hh;
      self.camera.updateProjectionMatrix();
      self.draw();
    };
    window.addEventListener('resize', this.onResize);

    /* **枠の大きさは window の resize だけでは変わらない。**
       パネルの出し入れや段組みの切り替えでも変わるので、枠そのものを見張る。
       見張らないと、描画バッファが初期化時の寸法のまま残って縦横比がずれる。 */
    if (window.ResizeObserver) {
      this.ro = new ResizeObserver(function () {
        self.onResize();
      });
      this.ro.observe(this.host);
    }
  };

  /*
    OrbitControls は使わない。examples/jsm は 'three' の裸指定子を import するので、
    import map なしでは解決できない。回転と拡大だけなのでここに書く。
  */
  Viewer.prototype.bindControls = function () {
    const el = this.renderer.domElement;
    const self = this;

    /*
      **触れている指を全部覚える。** 1本なら回転、2本ならピンチで拡大。
      1本目だけを見ていた頃は、2本目を置いた瞬間に「最後に触れた指の回転」になり、
      ピンチが模型を回してしまっていた（2026-08-20に対応）。
    */
    const pts = new Map();
    let pinch = 0;              /* 直前の2点間の距離[px]。0ならピンチ中でない */

    const twoDist = function () {
      const it = pts.values();
      const a = it.next().value;
      const b = it.next().value;
      return Math.hypot(a.x - b.x, a.y - b.y);
    };
    const zoom = function (factor) {
      self.orbit.dist = Math.min(2000, Math.max(40, self.orbit.dist * factor));
    };

    el.style.touchAction = 'none';
    el.addEventListener('pointerdown', function (e) {
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      el.setPointerCapture(e.pointerId);
      if (pts.size === 2) pinch = twoDist();
    });

    el.addEventListener('pointermove', function (e) {
      const prev = pts.get(e.pointerId);
      if (!prev) return;

      if (pts.size >= 2) {
        /* ピンチ中。**回転は止める。** 2本指で広げながら回すと目が回る */
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const d = twoDist();
        if (pinch > 0 && d > 0) zoom(pinch / d);
        pinch = d;
        self.draw();
        return;
      }

      self.orbit.az -= (e.clientX - prev.x) * 0.008;
      self.orbit.el = Math.min(
        1.45,
        Math.max(0.12, self.orbit.el + (e.clientY - prev.y) * 0.006)
      );
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      self.draw();
    });

    /* **離した指はその場で忘れる。** 残った指の位置は覚えたままなので、
       2本→1本に戻ったときに回転が飛ばない */
    const lift = function (e) {
      pts.delete(e.pointerId);
      if (pts.size < 2) pinch = 0;
    };
    el.addEventListener('pointerup', lift);
    el.addEventListener('pointercancel', lift);

    el.addEventListener(
      'wheel',
      function (e) {
        e.preventDefault();
        zoom(1 + Math.sign(e.deltaY) * 0.12);
        self.draw();
      },
      { passive: false }
    );
  };

  /*
    枠の寸法と描画バッファを突き合わせる。
    **初期化がレイアウト確定前に走ると、枠を0と読んで既定値のまま固まる。**
    ResizeObserver も「その後変化しない」ので拾えない。描く前と、
    収まる距離を決める前に呼ぶ（画角は縦横比で決まるので、順番が要る）。
  */
  Viewer.prototype.syncSize = function () {
    const cw = this.host.clientWidth;
    const ch = this.host.clientHeight;
    if (!(cw > 0 && ch > 0)) return;
    const size = this.renderer.getSize(new THREE.Vector2());
    if (Math.abs(size.x - cw) > 1 || Math.abs(size.y - ch) > 1) {
      this.renderer.setSize(cw, ch, false);
      this.camera.aspect = cw / ch;
      this.camera.updateProjectionMatrix();
    }
  };

  /*
    模型全体が画面に入る距離を決める。
    **長辺だけを見て距離を決めると見切れる。** 斜めから見ると対角が効くうえ、
    起伏と基部のぶん縦にも伸びる。外接球を作って、縦横どちらの画角にも
    収まる距離を取る。横長でない枠では横の画角が効くので、両方を見ること。
  */
  Viewer.prototype.fit = function () {
    this.group.position.set(0, 0, 0);
    const box = new THREE.Box3().setFromObject(this.group);
    if (box.isEmpty()) return;
    const center = box.getCenter(new THREE.Vector3());
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    this.group.position.sub(center);          // 中心を原点へ

    const vFov = (this.camera.fov * Math.PI) / 180;
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * this.camera.aspect);
    this.orbit.target = 0;
    this.orbit.dist = Math.max(
      sphere.radius / Math.sin(vFov / 2),
      sphere.radius / Math.sin(hFov / 2)
    );

    /* 外接球だけで決めると**小さくなりすぎる**。板状の模型では球の半径が
       箱の対角の半分になり、見た目の広がりよりずっと大きい。
       実際に投影して、画面のどこまで届くかを見て距離を詰める。
       球から始めているので、詰める向きにしか動かない（見切れない）。

       **箱の8隅ではなく、頂点そのものを見る。** 箱の上面はほとんど空で
       （最高地点だけが届く）、隅で測ると縦がそこに引っ張られて小さく収まる。
       頂点は間引いて拾う。的を92%に置いてあるので、多少取りこぼしても余る。 */
    const pts = [];
    this.group.children.forEach(function (m) {
      const a = m.geometry && m.geometry.getAttribute('position');
      if (!a) return;
      /* **間引きは奇数にすること。** 側面の頂点は「上端・下端」の対で並んでいるので、
         偶数で間引くと下端（y=0）だけを一度も拾わず、下辺が見切れる。 */
      let stride = Math.max(1, Math.floor(a.count / 1500));
      if (stride % 2 === 0) stride += 1;
      for (let i = 0; i < a.count; i += stride) {
        pts.push(new THREE.Vector3(a.getX(i), a.getY(i), a.getZ(i)).add(m.position));
      }
    });
    const off = this.group.position.clone();
    const TARGET = 0.92;                       // 画面のこの割合まで使う
    const probe = new THREE.Vector3();
    for (let k = 0; k < 4 && pts.length; k++) {
      this.placeCamera();
      this.camera.updateMatrixWorld();
      let m = 0;
      for (let i = 0; i < pts.length; i++) {
        probe.copy(pts[i]).add(off).project(this.camera);
        const ax = Math.abs(probe.x);
        const ay = Math.abs(probe.y);
        if (ax > m) m = ax;
        if (ay > m) m = ay;
      }
      if (!isFinite(m) || m <= 0) break;
      this.orbit.dist *= m / TARGET;
    }
  };

  /** orbit の値からカメラを置く。距離合わせでも使うので分けてある */
  Viewer.prototype.placeCamera = function () {
    const o = this.orbit;
    this.camera.position.set(
      Math.cos(o.el) * Math.sin(o.az) * o.dist,
      Math.sin(o.el) * o.dist,
      Math.cos(o.el) * Math.cos(o.az) * o.dist
    );
    this.camera.lookAt(0, o.target, 0);
  };

  /*
    描いた結果を見て距離を詰める。

    **投影計算だけでは詰めきれない。** 頂点を間引いて拾っているうえ、
    実際に見えている輪郭は頂点の並びと一致しない（面の被りや線の太さがある）。
    描いた画素を測って合わせるのが確実で、外れようがない。
    縮小した写しを1枚読むだけなので、数msで済む。
  */
  Viewer.prototype.fitByPixels = function () {
    const TARGET = 0.9;                        // 画面のこの割合に収める
    const vFov = (this.camera.fov * Math.PI) / 180;
    for (let k = 0; k < 4; k++) {
      const b = this.inkBounds();
      if (!b) return;

      /* **先に上下の位置を合わせる。** 縦にずれたまま縮めると、
         寄っている側に合わせて全体が小さくなる（実際そうなっていた）。
         見ている点を上げると、模型は画面の下へ動く。 */
      const cy = (b.y0 + b.y1) / 2 - 0.5;      // 正なら下寄り
      const frameH = 2 * this.orbit.dist * Math.tan(vFov / 2);
      if (Math.abs(cy) > 0.01) {
        this.orbit.target += -cy * frameH;
        this.draw();
        continue;
      }

      // 中心からの広がりを 0..1 で見て、目標との比で距離を動かす
      const hx = Math.max(Math.abs(b.x0 - 0.5), Math.abs(b.x1 - 0.5)) * 2;
      const hy = Math.max(Math.abs(b.y0 - 0.5), Math.abs(b.y1 - 0.5)) * 2;
      const m = Math.max(hx, hy);
      if (!isFinite(m) || m <= 0) return;
      if (Math.abs(m - TARGET) < 0.02) return; // 十分近ければ触らない
      this.orbit.dist *= m / TARGET;
      this.draw();
    }
  };

  /** 描画された画素の外接矩形を 0..1 で返す。何も描かれていなければ null */
  Viewer.prototype.inkBounds = function () {
    const src = this.renderer.domElement;
    if (!src.width || !src.height) return null;
    const W = 120;
    const H = Math.max(1, Math.round((src.height / src.width) * W));
    if (!this.probeCanvas) this.probeCanvas = document.createElement('canvas');
    const c = this.probeCanvas;
    c.width = W;
    c.height = H;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(src, 0, 0, W, H);
    const d = ctx.getImageData(0, 0, W, H).data;
    let minX = W, maxX = -1, minY = H, maxY = -1;
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        if (d[(y * W + x) * 4 + 3] > 10) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return null;
    return { x0: minX / W, x1: (maxX + 1) / W, y0: minY / H, y1: (maxY + 1) / H };
  };

  Viewer.prototype.draw = function () {
    this.syncSize();
    this.placeCamera();
    this.renderer.render(this.scene, this.camera);
  };

  Viewer.prototype.clear = function () {
    const self = this;
    this.meshes.forEach(function (m) {
      self.group.remove(m);
      m.geometry.dispose();
      m.material.dispose();
    });
    this.meshes = [];
  };

  Viewer.prototype.show = function (h, crop, tracks, colors) {
    this.clear();
    const mat = function (hex) {
      return new THREE.MeshStandardMaterial({
        color: new THREE.Color(hex || '#1A1A1C'),
        roughness: 0.95,
        metalness: 0.0,
        flatShading: false,
      });
    };

    const terrain = new THREE.Mesh(terrainGeometry(h), mat(colors.base));
    this.group.add(terrain);
    this.meshes.push(terrain);

    const self = this;
    routeGeometries(h, crop, tracks).forEach(function (g, i) {
      const m = new THREE.Mesh(g, mat(colors.route[i] || colors.route[0]));
      self.group.add(m);
      self.meshes.push(m);
    });

    // 全体が入る距離に置く。**画角は縦横比で決まるので、先に寸法を合わせる**
    this.syncSize();
    this.fit();
    this.draw();
    this.fitByPixels();
  };

  Viewer.prototype.setColors = function (colors) {
    // 色替えは材質だけ差し替える。形は作り直さない
    this.meshes.forEach(function (m, i) {
      const hex = i === 0 ? colors.base : colors.route[i - 1] || colors.route[0];
      if (hex) m.material.color.set(hex);
    });
    this.draw();
  };

  /*
    ステップ2で先に読んでおく。**ステップ3の待ちを消すため。**
    実測（有線）では Three.js 1,243KB / 198ms、標高タイルは余白800mで6枚 615KB / 121ms。
    速い回線では効果は小さいが、携帯回線では合計1.8MBぶんの待ちが前に寄る。

    **余白を大きめに見積もって取りに行かないこと。** 余白2,500mだとタイルが20枚
    （既定800mの3倍以上）になり、大半は使われずに終わる。いまの切り出し範囲だけ取る。
    余白を広げられたら、増えたぶんだけステップ3で取れば済む（タイル単位でキャッシュ済み）。
  */
  function prefetch(crop) {
    loadThree().catch(function () {});
    if (!crop) return;
    const x0 = Math.floor(lonToTileX(crop.west, DEM_Z));
    const x1 = Math.floor(lonToTileX(crop.east, DEM_Z));
    const y0 = Math.floor(latToTileY(crop.north, DEM_Z));
    const y1 = Math.floor(latToTileY(crop.south, DEM_Z));
    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) fetchTile(DEM_Z, tx, ty);
    }
  }

  window.R3DPreview3D = {
    load: loadThree,
    build: buildHeight,
    stats: terrainStats,
    prefetch: prefetch,
    Viewer: Viewer,
    BASE_MM: BASE_MM,
  };
})();
